import { useRef, useState, type MouseEvent, type ReactNode } from "react";

/**
 * Lightweight hand-rolled SVG charts for the dashboard analytics widgets
 * (daily wins/losses, daily P&L, drawdown, cumulative P&L). These replaced
 * `lightweight-charts` for non-price data: that library is reserved for
 * actual instrument price/time series (see FillTimelineChart).
 *
 * The SVG draws in a fixed `0 0 1000 192` viewBox and stretches to the
 * container via `preserveAspectRatio="none"`; strokes use
 * `vector-effect="non-scaling-stroke"` so line weight stays constant under
 * the horizontal scale. Colors reference theme CSS variables directly, so the
 * charts react to theme switches with no JS.
 *
 * `fill` makes the chart fill its container height (dashboard tiles) instead
 * of the fixed default height (standalone page usage). When `labels` +
 * `formatValue` are supplied the chart shows a hover tooltip with the exact
 * value + label for the data point under the cursor (issue 5).
 */

export const CHART_HEIGHT_PX = 192;
const VB_W = 1000;
const VB_H = CHART_HEIGHT_PX;
const PAD_Y = 12;
// Horizontal inset for line series so the first/last vertices -- and the round
// hover marker that snaps to them -- sit inside the plot instead of half-
// clipped against the widget edge. Bars are already inset by half a slot.
const PAD_X = 16;

// X (viewBox units) of the i-th of n line vertices, inset by PAD_X on each side.
function lineX(i: number, n: number): number {
  return n <= 1 ? VB_W / 2 : PAD_X + (i / (n - 1)) * (VB_W - 2 * PAD_X);
}

function domainWithZero(values: readonly number[]): { min: number; max: number } {
  let min = 0;
  let max = 0;
  for (const v of values) {
    if (v < min) min = v;
    if (v > max) max = v;
  }
  if (min === 0 && max === 0) max = 1;
  return { min, max };
}

function yScale(v: number, min: number, max: number): number {
  const range = max - min || 1;
  return PAD_Y + (1 - (v - min) / range) * (VB_H - 2 * PAD_Y);
}

function formatCompact(n: number): string {
  return new Intl.NumberFormat(undefined, {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(n);
}

export interface HoverModel {
  count: number;
  indexForXPct: (xPct: number) => number;
  xPctForIndex: (index: number) => number;
  yPctForIndex: (index: number) => number;
  labelForIndex: (index: number) => string;
  valueForIndex: (index: number) => string;
}

function buildHover(
  values: readonly number[],
  labels: readonly string[] | undefined,
  formatValue: ((v: number) => string) | undefined,
  kind: "bar" | "line",
  yScaleFn: (v: number) => number,
): HoverModel | undefined {
  if (labels === undefined || formatValue === undefined || values.length === 0) {
    return undefined;
  }
  const n = values.length;
  return {
    count: n,
    indexForXPct: (p) => {
      if (kind === "bar") return Math.min(n - 1, Math.max(0, Math.floor(p * n)));
      if (n <= 1) return 0;
      // Invert lineX: map the container x-fraction back through the PAD_X inset.
      const frac = (p * VB_W - PAD_X) / (VB_W - 2 * PAD_X);
      return Math.min(n - 1, Math.max(0, Math.round(frac * (n - 1))));
    },
    xPctForIndex: (i) =>
      kind === "bar" ? (i + 0.5) / n : lineX(i, n) / VB_W,
    // Vertical position of the data point, as a percent of container height.
    // The SVG uses preserveAspectRatio="none", so a viewBox y maps linearly
    // to the rendered height -- this places the marker dot exactly where the
    // hover guide meets the series (issue: chart hover intercept marker).
    yPctForIndex: (i) => (yScaleFn(values[i] ?? 0) / VB_H) * 100,
    labelForIndex: (i) => labels[i] ?? "",
    valueForIndex: (i) => formatValue(values[i] ?? 0),
  };
}

function ChartShell({
  testId,
  hasData,
  max,
  min,
  fill = false,
  hover,
  hideMinMax = false,
  children,
}: {
  testId: string;
  hasData: boolean;
  max: number;
  min: number;
  fill?: boolean;
  hover?: HoverModel;
  // Suppress the built-in corner max/min labels when the caller draws its own
  // axis (e.g. the dashboard Cumulative P&L hero).
  hideMinMax?: boolean;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState<number | null>(null);

  const handleMove = (event: MouseEvent<HTMLDivElement>) => {
    if (hover === undefined || hover.count === 0 || ref.current === null) return;
    const rect = ref.current.getBoundingClientRect();
    if (rect.width === 0) return;
    const xPct = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    setActive(hover.indexForXPct(xPct));
  };
  const handleLeave = () => setActive(null);

  const activeFrac =
    hover !== undefined && active !== null ? hover.xPctForIndex(active) : null;
  const activeLeft =
    activeFrac !== null ? `${(activeFrac * 100).toFixed(2)}%` : undefined;
  // Anchor the tooltip inside the plot near the edges so the box isn't clipped
  // by the widget's rounded card: left-aligned at the start, right-aligned at
  // the end, centred in between. The guide line + marker stay on the point.
  const tooltipShift =
    activeFrac === null
      ? "-translate-x-1/2"
      : activeFrac < 0.12
        ? "translate-x-0"
        : activeFrac > 0.88
          ? "-translate-x-full"
          : "-translate-x-1/2";
  const activeTop =
    hover !== undefined && active !== null
      ? `${hover.yPctForIndex(active).toFixed(2)}%`
      : undefined;

  return (
    <div
      ref={ref}
      className={`relative w-full ${fill ? "h-full" : ""}`}
      style={fill ? undefined : { height: CHART_HEIGHT_PX }}
      data-testid={testId}
      onMouseMove={hover !== undefined ? handleMove : undefined}
      onMouseLeave={hover !== undefined ? handleLeave : undefined}
    >
      {hasData ? (
        <>
          <svg
            viewBox={`0 0 ${VB_W} ${VB_H}`}
            preserveAspectRatio="none"
            className="h-full w-full"
            aria-hidden="true"
          >
            {children}
          </svg>
          {hideMinMax ? null : (
            <>
              <span className="pointer-events-none absolute left-1 top-0.5 text-[10px] tabular-nums text-on-surface-muted">
                {formatCompact(max)}
              </span>
              <span className="pointer-events-none absolute bottom-0.5 left-1 text-[10px] tabular-nums text-on-surface-muted">
                {formatCompact(min)}
              </span>
            </>
          )}
          {hover !== undefined && active !== null && activeLeft !== undefined ? (
            <>
              <div
                className="pointer-events-none absolute inset-y-0 w-px bg-on-surface-muted/40 transition-[left] duration-150 ease-out motion-reduce:transition-none"
                style={{ left: activeLeft }}
              />
              {activeTop !== undefined ? (
                <div
                  aria-hidden="true"
                  data-testid={`${testId}-marker`}
                  className="pointer-events-none absolute h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-surface bg-on-surface shadow transition-[left,top] duration-150 ease-out motion-reduce:transition-none"
                  style={{ left: activeLeft, top: activeTop }}
                />
              ) : null}
              <div
                role="status"
                data-testid={`${testId}-tooltip`}
                className={`pointer-events-none absolute top-1 z-10 ${tooltipShift} whitespace-nowrap rounded border border-outline-variant bg-surface-container-high px-2 py-1 text-[10px] shadow-lg transition-[left] duration-150 ease-out motion-reduce:transition-none`}
                style={{ left: activeLeft }}
              >
                <div className="font-semibold tabular-nums text-on-surface">
                  {hover.valueForIndex(active)}
                </div>
                <div className="text-on-surface-variant">
                  {hover.labelForIndex(active)}
                </div>
              </div>
            </>
          ) : null}
        </>
      ) : (
        <p className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-on-surface-muted">
          No data to display
        </p>
      )}
    </div>
  );
}

export function BarMiniChart({
  values,
  testId,
  labels,
  formatValue,
  fill,
  hideMinMax,
}: {
  values: readonly number[];
  testId: string;
  labels?: readonly string[];
  formatValue?: (v: number) => string;
  fill?: boolean;
  hideMinMax?: boolean;
}) {
  const { min, max } = domainWithZero(values);
  const zeroY = yScale(0, min, max);
  const n = values.length;
  const slotW = n > 0 ? VB_W / n : VB_W;
  const barW = slotW * 0.62;
  const hover = buildHover(values, labels, formatValue, "bar", (v) =>
    yScale(v, min, max),
  );
  return (
    <ChartShell
      testId={testId}
      hasData={n > 0}
      max={max}
      min={min}
      fill={fill}
      hover={hover}
      hideMinMax={hideMinMax}
    >
      <line
        x1={0}
        y1={zeroY}
        x2={VB_W}
        y2={zeroY}
        stroke="var(--color-outline-variant)"
        strokeWidth={1}
        vectorEffect="non-scaling-stroke"
      />
      {values.map((v, i) => {
        const y = yScale(v, min, max);
        const top = Math.min(y, zeroY);
        const h = Math.max(Math.abs(zeroY - y), 0.75);
        const x = i * slotW + (slotW - barW) / 2;
        return (
          <rect
            key={i}
            x={x}
            y={top}
            width={barW}
            height={h}
            rx={1}
            fill={v >= 0 ? "var(--color-success)" : "var(--color-danger)"}
          />
        );
      })}
    </ChartShell>
  );
}

export function DivergingBarMiniChart({
  wins,
  losses,
  testId,
  labels,
  fill,
}: {
  wins: readonly number[];
  losses: readonly number[];
  testId: string;
  labels?: readonly string[];
  fill?: boolean;
}) {
  const n = Math.min(wins.length, losses.length);
  const maxWin = Math.max(0, ...wins);
  const maxLoss = Math.max(0, ...losses);
  // Symmetric magnitude so an N-win bar and an N-loss bar are the same
  // height -- the diverging axis reads as "wins up vs losses down".
  const magnitude = Math.max(1, maxWin, maxLoss);
  const yFor = (v: number) =>
    PAD_Y + (1 - (v + magnitude) / (2 * magnitude)) * (VB_H - 2 * PAD_Y);
  const zeroY = yFor(0);
  const slotW = n > 0 ? VB_W / n : VB_W;
  const barW = slotW * 0.62;

  const ref = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState<number | null>(null);
  const handleMove = (event: MouseEvent<HTMLDivElement>) => {
    if (n === 0 || ref.current === null) return;
    const rect = ref.current.getBoundingClientRect();
    if (rect.width === 0) return;
    const xPct = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    setActive(Math.min(n - 1, Math.max(0, Math.floor(xPct * n))));
  };
  const handleLeave = () => setActive(null);
  const activeFrac = active !== null ? (active + 0.5) / n : null;
  const activeLeft =
    activeFrac !== null ? `${(activeFrac * 100).toFixed(2)}%` : undefined;
  const tooltipShift =
    activeFrac === null
      ? "-translate-x-1/2"
      : activeFrac < 0.12
        ? "translate-x-0"
        : activeFrac > 0.88
          ? "-translate-x-full"
          : "-translate-x-1/2";

  return (
    <div
      ref={ref}
      className={`relative w-full ${fill ? "h-full" : ""}`}
      style={fill ? undefined : { height: CHART_HEIGHT_PX }}
      data-testid={testId}
      onMouseMove={labels !== undefined ? handleMove : undefined}
      onMouseLeave={labels !== undefined ? handleLeave : undefined}
    >
      {n > 0 ? (
        <>
          <svg
            viewBox={`0 0 ${VB_W} ${VB_H}`}
            preserveAspectRatio="none"
            className="h-full w-full"
            aria-hidden="true"
          >
            <line
              x1={0}
              y1={zeroY}
              x2={VB_W}
              y2={zeroY}
              stroke="var(--color-outline-variant)"
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
            />
            {Array.from({ length: n }, (_, i) => {
              const x = i * slotW + (slotW - barW) / 2;
              const winTop = yFor(wins[i] ?? 0);
              const lossBottom = yFor(-(losses[i] ?? 0));
              return (
                <g key={i}>
                  {(wins[i] ?? 0) > 0 ? (
                    <rect
                      x={x}
                      y={winTop}
                      width={barW}
                      height={Math.max(zeroY - winTop, 0.75)}
                      rx={1}
                      fill="var(--color-success)"
                    />
                  ) : null}
                  {(losses[i] ?? 0) > 0 ? (
                    <rect
                      x={x}
                      y={zeroY}
                      width={barW}
                      height={Math.max(lossBottom - zeroY, 0.75)}
                      rx={1}
                      fill="var(--color-danger)"
                    />
                  ) : null}
                </g>
              );
            })}
          </svg>
          <span className="pointer-events-none absolute left-1 top-0.5 text-[10px] tabular-nums text-on-surface-muted">
            {formatCompact(maxWin)}W
          </span>
          <span className="pointer-events-none absolute bottom-0.5 left-1 text-[10px] tabular-nums text-on-surface-muted">
            {formatCompact(maxLoss)}L
          </span>
          {labels !== undefined && active !== null && activeLeft !== undefined ? (
            <>
              <div
                className="pointer-events-none absolute inset-y-0 w-px bg-on-surface-muted/40 transition-[left] duration-150 ease-out motion-reduce:transition-none"
                style={{ left: activeLeft }}
              />
              <div
                role="status"
                data-testid={`${testId}-tooltip`}
                className={`pointer-events-none absolute top-1 z-10 ${tooltipShift} whitespace-nowrap rounded border border-outline-variant bg-surface-container-high px-2 py-1 text-[10px] shadow-lg transition-[left] duration-150 ease-out motion-reduce:transition-none`}
                style={{ left: activeLeft }}
              >
                <div className="font-semibold tabular-nums text-on-surface">
                  {wins[active] ?? 0}W / {losses[active] ?? 0}L
                </div>
                <div className="text-on-surface-variant">{labels[active] ?? ""}</div>
              </div>
            </>
          ) : null}
        </>
      ) : (
        <p className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-on-surface-muted">
          No data to display
        </p>
      )}
    </div>
  );
}

export function LineMiniChart({
  values,
  testId,
  tone,
  labels,
  formatValue,
  fill,
  hideMinMax,
}: {
  values: readonly number[];
  testId: string;
  tone: "primary" | "danger" | "success";
  labels?: readonly string[];
  formatValue?: (v: number) => string;
  fill?: boolean;
  hideMinMax?: boolean;
}) {
  const { min, max } = domainWithZero(values);
  const zeroY = yScale(0, min, max);
  const n = values.length;
  const color =
    tone === "danger"
      ? "var(--color-danger)"
      : tone === "success"
        ? "var(--color-success)"
        : "var(--color-primary)";
  const pts: ReadonlyArray<readonly [number, number]> = values.map(
    (v, i) => [lineX(i, n), yScale(v, min, max)] as const,
  );
  const first = pts[0];
  const last = pts[pts.length - 1];
  const linePath = pts
    .map((p, i) => `${i === 0 ? "M" : "L"} ${p[0].toFixed(2)} ${p[1].toFixed(2)}`)
    .join(" ");
  const areaPath =
    first && last
      ? `${linePath} L ${last[0].toFixed(2)} ${zeroY.toFixed(2)} L ${first[0].toFixed(2)} ${zeroY.toFixed(2)} Z`
      : "";
  const hover = buildHover(values, labels, formatValue, "line", (v) =>
    yScale(v, min, max),
  );
  return (
    <ChartShell
      testId={testId}
      hasData={n > 0}
      max={max}
      min={min}
      fill={fill}
      hover={hover}
      hideMinMax={hideMinMax}
    >
      <line
        x1={0}
        y1={zeroY}
        x2={VB_W}
        y2={zeroY}
        stroke="var(--color-outline-variant)"
        strokeWidth={1}
        vectorEffect="non-scaling-stroke"
      />
      {n === 1 && first ? (
        <circle cx={first[0]} cy={first[1]} r={3} fill={color} />
      ) : (
        <>
          <path d={areaPath} fill={color} fillOpacity={0.12} stroke="none" />
          <path
            d={linePath}
            fill="none"
            stroke={color}
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
        </>
      )}
    </ChartShell>
  );
}
